import React, { Dispatch, SetStateAction, useMemo, useRef, useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Fade, Popper } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ExpandedSummaryModal from './ExpandedSummaryModal';
import dompurify from 'dompurify';
import { classes } from './styles';
import { StyledSnackbarProps } from '../../NewUI/Components/CustomUIElements/StyledSnackbar';
import { FelixConfused, FelixMessageSVG } from '../SharedComponents/Graphics/FelixSVG';
import { IApplicationInfo } from '../SharedComponents/types';
import { convertToLocalEnglish } from '../../NewUI/Components/Utilities/convertToLocalEnglish';
import { useWebSockets } from '../../NewUI/Components/Utilities/useWebSockets';
import { IWebsocketData } from '../SharedComponents/types';

const SummaryPopover = ({
  applicantHovered,
  handleHoverApplicant,
  handleUnhoverApplicant,
  lastHoveredApplicant,
  anchorEl,
  jobId,
  setSnackbarState,
  summaryGeneratingIds,
  setSummaryGeneratingIds,
  refetchApplications
}: {
  applicantHovered: boolean;
  handleHoverApplicant: (target: HTMLElement | null, application?: IApplicationInfo) => void;
  handleUnhoverApplicant: () => void;
  lastHoveredApplicant: IApplicationInfo;
  anchorEl: HTMLElement | null;
  jobId: number;
  setSnackbarState: StyledSnackbarProps['setSnackbarState'];
  summaryGeneratingIds: number[];
  setSummaryGeneratingIds: Dispatch<SetStateAction<number[]>>;
  refetchApplications?: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const [summaryDataMap, setSummaryDataMap] = useState<Map<number, IWebsocketData>>(new Map());
  const [modalGenerating, setModalGenerating] = useState(false);
  const [savedApplicationIds, setSavedApplicationIds] = useState<number[]>([]);
  const [generatingApplicationId, setGeneratingApplicationId] = useState<number | null>(null);

  const summaryData = summaryDataMap.get(lastHoveredApplicant.id || -1) || null;

  const [dotPoints, summary] = useMemo(() => {
    if (!lastHoveredApplicant?.summary) return [null, null];
    const summary = convertToLocalEnglish(lastHoveredApplicant.summary);
    const ulIndex = summary.indexOf('<ul>');

    if (ulIndex !== -1) {
      const listItems = summary
        ?.replace('\n', '')
        .split('<ul>')[1]
        .split('</li>')
        .slice(0, 3)
        .join('</li>');
      return ['<ul>' + listItems + '</li></ul>', summary];
    } else {
      // Handle the case where <ul> is not found in the summary
      return [summary, summary];
    }
  }, [lastHoveredApplicant.summary]);

  const { subscribeToChannel, unsubscribeFromChannel, socketConnected } = useWebSockets(
    'AiStudioChannel',
    (data: IWebsocketData, UID?: number) => {
      if (data.UID !== UID) return;
      if (data.kind === 'error') {
        setSnackbarState({
          message: 'Something went wrong, please refresh and try again',
          state: 'error'
        });
        setSummaryGeneratingIds((prev) => prev.filter((id) => id !== generatingApplicationId));
        setModalGenerating(false);
        setGeneratingApplicationId(null);
      } else {
        setSummaryDataMap((prev) => {
          const newMap = new Map(prev);
          newMap.set(generatingApplicationId || lastHoveredApplicant.id || -1, data);
          return newMap;
        });
        setSummaryGeneratingIds((prev) => prev.filter((id) => id !== generatingApplicationId));
        setModalGenerating(false);
        setGeneratingApplicationId(null);
      }
      unsubscribeFromChannel();
    }
  );

  const handleCloseModal = () => {
    if (!generatingApplicationId) {
      unsubscribeFromChannel();
      setModalGenerating(false);
    }
    if (!summaryData?.id) {
      setSummaryDataMap((prev) => {
        const newMap = new Map(prev);
        newMap.delete(lastHoveredApplicant.id || -1);
        return newMap;
      });
    }
    setExpanded(false);
  };

  const generateSummary = async (feedbackInput?: string) => {
    setLoadingSummary(true);
    setModalGenerating(true);
    const UID = Date.now();
    const targetApplicationId = lastHoveredApplicant.id as number;
    try {
      const request = await fetch(
        `/api/v4/jobs/${jobId}/applications/${targetApplicationId}/application_summary`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            UID: UID,
            ...(feedbackInput && { feedback: feedbackInput })
          })
        }
      );
      if (request.ok) {
        setSnackbarState({
          message: 'Summary is being generated, this may take a few minutes',
          state: 'success'
        });
        setSummaryGeneratingIds((prev) => [...prev, targetApplicationId]);
        setSavedApplicationIds((prev) => prev.filter((id) => id !== targetApplicationId));
        setGeneratingApplicationId(targetApplicationId);
        subscribeToChannel(UID);
      } else {
        const response = await request.json();
        setSnackbarState({
          message: response.errors || 'Error requesting a generated summary',
          state: 'error'
        });
        setModalGenerating(false);
      }
    } catch (e) {
      setSnackbarState({
        message: 'Something went wrong, please refresh and try again',
        state: 'error'
      });
      setModalGenerating(false);
    } finally {
      setLoadingSummary(false);
    }
  };

  const updateSummary = async () => {
    try {
      const request = await fetch(`/api/v4/jobs/${jobId}/application_summaries/update_summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          application_summary: {
            history_id: summaryData?.id
          }
        })
      });
      if (request.ok) {
        setSnackbarState({
          message: 'Candidate summary saved successfully',
          state: 'success'
        });
        setSavedApplicationIds((prev) =>
          prev.includes(lastHoveredApplicant.id as number)
            ? prev
            : [...prev, lastHoveredApplicant.id as number]
        );
      } else {
        const response = await request.json();
        setSnackbarState({
          message: response.errors || 'Error requesting a generated summary',
          state: 'error'
        });
      }
    } catch (e) {
      setSnackbarState({
        message: 'Something went wrong, please refresh and try again',
        state: 'error'
      });
    }
  };

  const isGeneratingSummary = useMemo(
    () => summaryGeneratingIds.includes(lastHoveredApplicant.id || -1),
    [summaryGeneratingIds, lastHoveredApplicant.id]
  );

  const isModalLoading = useMemo(
    () => socketConnected && modalGenerating && isGeneratingSummary,
    [socketConnected, modalGenerating, isGeneratingSummary]
  );

  const isApplicationSaved = useMemo(
    () => savedApplicationIds.includes(lastHoveredApplicant.id || -1),
    [savedApplicationIds, lastHoveredApplicant.id]
  );

  useEffect(() => {
    return () => {
      unsubscribeFromChannel();
    };
  }, [unsubscribeFromChannel]);

  return (
    <>
      <Popper
        id={'application-summary-popper'}
        open={applicantHovered}
        anchorEl={anchorEl}
        placement="right"
        transition
        sx={classes.summaryPopper}
        onMouseEnter={() => handleHoverApplicant(anchorEl)}
        onMouseLeave={handleUnhoverApplicant}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={300}>
            <Box sx={classes.summaryPopover}>
              {summary ? (
                <>
                  <OpenInFullIcon
                    id="expand-AI-summary-button"
                    sx={classes.expandIcon}
                    onClick={() => setExpanded(true)}
                  />
                  <div dangerouslySetInnerHTML={{ __html: dompurify.sanitize(dotPoints) }} />
                </>
              ) : (
                <>
                  <Box sx={classes.noSummaryFelix}>
                    {isGeneratingSummary ? <FelixMessageSVG /> : <FelixConfused />}
                  </Box>
                  <Box sx={classes.noSummary}>
                    {isGeneratingSummary ? 'Summary being generated' : 'No summary available'}
                  </Box>
                  <Box sx={classes.noSummaryGenerate}>
                    {!isGeneratingSummary ? (
                      <Button sx={classes.pillButton} onClick={() => generateSummary('')}>
                        {loadingSummary ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          'Request summary'
                        )}
                      </Button>
                    ) : (
                      <Box sx={classes.noSummary}>Please check back in a few minutes</Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Fade>
        )}
      </Popper>
      {summary && (
        <ExpandedSummaryModal
          jobId={jobId}
          expanded={expanded}
          handleCloseModal={handleCloseModal}
          lastHoveredApplicant={lastHoveredApplicant}
          summaryRef={summaryRef}
          setCopied={setCopied}
          copied={copied}
          loadingSummary={loadingSummary}
          generateSummary={generateSummary}
          setSnackbarState={setSnackbarState}
          isLoading={isModalLoading}
          updateSummary={updateSummary}
          summaryData={summaryData}
          refetchApplications={refetchApplications}
          isApplicationSaved={isApplicationSaved}
          isGeneratingSummary={isGeneratingSummary}
        />
      )}
    </>
  );
};

export default SummaryPopover;
